import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AutocompleteHookReturn, AutocompleteItem, AutocompletePosition } from "./types";
export const useAutocomplete = (value, items, t0, t1, onInputChange, onItemSelect, onClear, onValueSelectedChange) => {
  const $ = _c(44);
  const isReady = t0 === undefined ? true : t0;
  const isValueSelected = t1 === undefined ? false : t1;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 0,
      left: 0,
      width: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [dropdownPosition, setDropdownPosition] = useState(t2);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const updateDropdownPosition = t3;
  let t4;
  if ($[2] !== isReady || $[3] !== isValueSelected || $[4] !== onInputChange || $[5] !== onValueSelectedChange) {
    t4 = inputValue => {
      if (isValueSelected && onValueSelectedChange) {
        onValueSelectedChange(false);
      }
      onInputChange(inputValue);
      if (inputValue.trim() && isReady && !isValueSelected) {
        setIsDropdownOpen(true);
        updateDropdownPosition();
      } else {
        setIsDropdownOpen(false);
      }
    };
    $[2] = isReady;
    $[3] = isValueSelected;
    $[4] = onInputChange;
    $[5] = onValueSelectedChange;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleInputChangeWithDropdown = t4;
  let t5;
  if ($[7] !== items || $[8] !== onItemSelect || $[9] !== onValueSelectedChange) {
    t5 = selectedId => {
      const selectedItem = items.find(item => item.id === selectedId);
      if (selectedItem && !selectedItem.isPlaceholder) {
        onItemSelect(selectedId);
        setIsDropdownOpen(false);
        if (onValueSelectedChange) {
          onValueSelectedChange(true);
        }
      }
    };
    $[7] = items;
    $[8] = onItemSelect;
    $[9] = onValueSelectedChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleItemSelect = t5;
  let t6;
  if ($[11] !== onClear || $[12] !== onValueSelectedChange) {
    t6 = () => {
      onClear();
      setIsDropdownOpen(false);
      if (onValueSelectedChange) {
        onValueSelectedChange(false);
      }
    };
    $[11] = onClear;
    $[12] = onValueSelectedChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleClearInput = t6;
  let t7;
  if ($[14] !== isReady || $[15] !== isValueSelected || $[16] !== items.length || $[17] !== value) {
    t7 = () => {
      if (typeof value === "string" && value?.trim() && items.length > 0 && isReady && !isValueSelected) {
        setIsDropdownOpen(true);
        updateDropdownPosition();
      }
    };
    $[14] = isReady;
    $[15] = isValueSelected;
    $[16] = items.length;
    $[17] = value;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleInputFocus = t7;
  let t8;
  if ($[19] !== isReady || $[20] !== isValueSelected || $[21] !== items[0] || $[22] !== items.length || $[23] !== value) {
    t8 = () => {
      if (items.length === 0 || items.length === 1 && items[0].isPlaceholder) {
        if (!value || typeof value !== "string" || !value.trim()) {
          setIsDropdownOpen(false);
        }
      } else {
        if (items.length > 0 && typeof value === "string" && value.trim() && isReady && !isValueSelected) {
          setIsDropdownOpen(true);
          updateDropdownPosition();
        }
      }
    };
    $[19] = isReady;
    $[20] = isValueSelected;
    $[21] = items[0];
    $[22] = items.length;
    $[23] = value;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isReady || $[26] !== isValueSelected || $[27] !== items || $[28] !== value) {
    t9 = [items, value, isReady, isValueSelected, updateDropdownPosition];
    $[25] = isReady;
    $[26] = isValueSelected;
    $[27] = items;
    $[28] = value;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[30] !== isDropdownOpen) {
    t10 = () => {
      const handleScrollOrResize = () => {
        if (isDropdownOpen) {
          updateDropdownPosition();
        }
      };
      window.addEventListener("scroll", handleScrollOrResize, {
        passive: true
      });
      window.addEventListener("resize", handleScrollOrResize, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    };
    t11 = [isDropdownOpen, updateDropdownPosition];
    $[30] = isDropdownOpen;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const handleEscape = e => {
        if (e.key === "Escape") {
          setIsDropdownOpen(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t13 = [];
    $[33] = t12;
    $[34] = t13;
  } else {
    t12 = $[33];
    t13 = $[34];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      const handleClickOutside = event => {
        if (dropdownRef.current && inputRef.current && !dropdownRef.current.contains(event.target as Node) && !inputRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t15 = [];
    $[35] = t14;
    $[36] = t15;
  } else {
    t14 = $[35];
    t15 = $[36];
  }
  useEffect(t14, t15);
  let t16;
  if ($[37] !== dropdownPosition || $[38] !== handleClearInput || $[39] !== handleInputChangeWithDropdown || $[40] !== handleInputFocus || $[41] !== handleItemSelect || $[42] !== isDropdownOpen) {
    t16 = {
      inputRef,
      dropdownRef,
      isDropdownOpen,
      dropdownPosition,
      handleInputChangeWithDropdown,
      handleItemSelect,
      handleClearInput,
      handleInputFocus
    };
    $[37] = dropdownPosition;
    $[38] = handleClearInput;
    $[39] = handleInputChangeWithDropdown;
    $[40] = handleInputFocus;
    $[41] = handleItemSelect;
    $[42] = isDropdownOpen;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};